// frontend/src/pages/admin/Settings.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import {
    RefreshCw, Save, DollarSign, Trophy, Clock, Gamepad2, CheckCircle, AlertCircle
} from 'lucide-react';

// ─── Componente campo ─────────────────────────────────────────────────────────
const Field = ({ label, description, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-sm font-semibold text-white">{label}</label>
        {description && <p className="text-xs text-gray-500">{description}</p>}
        {children}
    </div>
);

const Input = ({ value, onChange, type = 'number', min, step, suffix }) => (
    <div className="flex items-center gap-2">
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            min={min}
            step={step}
            className="rounded-lg px-3 py-2.5 text-sm text-white border-0 focus:ring-1 focus:ring-blue-500 w-36"
            style={{ backgroundColor: '#122436' }}
        />
        {suffix && <span className="text-sm text-gray-400">{suffix}</span>}
    </div>
);

// ─── Página principal ─────────────────────────────────────────────────────────
const Settings = () => {
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState('');
    const [success,  setSuccess]  = useState('');

    const [form, setForm] = useState({
        ticketPrice:           '',
        tournamentInterval:    '',   // en segundos → mostrar en minutos
        maxConcurrentTourneys: '',
        readyTimeout:          '',   // en segundos
    });

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get('/admin/settings');
                if (res.data.success) {
                    const d = res.data.data;
                    setForm({
                        ticketPrice:           d.ticketPrice           ?? 1.0,
                        tournamentInterval:    Math.round((d.tournamentInterval ?? 3600) / 60), // s → min
                        maxConcurrentTourneys: d.maxConcurrentTourneys ?? 3,
                        readyTimeout:          d.readyTimeout          ?? 300,
                    });
                }
            } catch (err) {
                setError('No se pudo cargar la configuración.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const set = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = {
                ticketPrice:           Number(form.ticketPrice),
                tournamentInterval:    Number(form.tournamentInterval) * 60, // min → s
                maxConcurrentTourneys: Number(form.maxConcurrentTourneys),
                readyTimeout:          Number(form.readyTimeout),
            };
            const res = await axios.put('/admin/settings', payload);
            if (res.data.success) {
                setSuccess('¡Configuración guardada exitosamente!');
                setTimeout(() => setSuccess(''), 4000);
            } else {
                setError(res.data.message || 'Error al guardar.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-2xl">

            {/* ── Encabezado ──────────────────────────────────────────────── */}
            <div>
                <h1 className="text-2xl font-bold text-white">Configuración</h1>
                <p className="text-sm text-gray-500 mt-0.5">Parámetros globales del sistema</p>
            </div>

            {/* ── Mensajes ────────────────────────────────────────────────── */}
            {error && (
                <div className="flex items-center gap-2 p-4 rounded-lg text-red-400 text-sm"
                     style={{ backgroundColor: '#2d1a1a' }}>
                    <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
            )}
            {success && (
                <div className="flex items-center gap-2 p-4 rounded-lg text-green-400 text-sm"
                     style={{ backgroundColor: '#132d1a' }}>
                    <CheckCircle className="w-4 h-4 shrink-0" /> {success}
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">

                {/* ── Economía ────────────────────────────────────────────── */}
                <div className="rounded-xl p-6 space-y-5" style={{ backgroundColor: '#0E2238' }}>
                    <h2 className="font-semibold text-white flex items-center gap-2">
                        <DollarSign className="w-4 h-4" style={{ color: '#22c55e' }} />
                        Economía
                    </h2>

                    <Field
                        label="Precio del ticket"
                        description="Valor en ARS de cada ticket. Se usa para mostrar el precio de compra a los usuarios."
                    >
                        <Input
                            value={form.ticketPrice}
                            onChange={set('ticketPrice')}
                            min="0"
                            step="0.01"
                            suffix="ARS / ticket"
                        />
                    </Field>
                </div>

                {/* ── Torneos ─────────────────────────────────────────────── */}
                <div className="rounded-xl p-6 space-y-5" style={{ backgroundColor: '#0E2238' }}>
                    <h2 className="font-semibold text-white flex items-center gap-2">
                        <Trophy className="w-4 h-4" style={{ color: '#FCE503' }} />
                        Torneos
                    </h2>

                    <Field
                        label="Torneos simultáneos máx."
                        description="Cantidad máxima de torneos que pueden estar activos (en estado REGISTERING o IN_PROGRESS) al mismo tiempo."
                    >
                        <Input
                            value={form.maxConcurrentTourneys}
                            onChange={set('maxConcurrentTourneys')}
                            min="1"
                            step="1"
                            suffix="torneos"
                        />
                    </Field>

                    <Field
                        label="Intervalo entre torneos recurrentes"
                        description="Tiempo mínimo en minutos entre la creación de torneos recurrentes del mismo tipo."
                    >
                        <Input
                            value={form.tournamentInterval}
                            onChange={set('tournamentInterval')}
                            min="1"
                            step="1"
                            suffix="minutos"
                        />
                    </Field>
                </div>

                {/* ── Matches ─────────────────────────────────────────────── */}
                <div className="rounded-xl p-6 space-y-5" style={{ backgroundColor: '#0E2238' }}>
                    <h2 className="font-semibold text-white flex items-center gap-2">
                        <Gamepad2 className="w-4 h-4" style={{ color: '#2284DF' }} />
                        Matches
                    </h2>

                    <Field
                        label="Timeout del ready check"
                        description="Segundos que tiene cada jugador para confirmar estar listo antes de que el match sea cancelado."
                    >
                        <Input
                            value={form.readyTimeout}
                            onChange={set('readyTimeout')}
                            min="30"
                            step="30"
                            suffix="segundos"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                            = {Math.floor(form.readyTimeout / 60)} min {form.readyTimeout % 60} s
                        </p>
                    </Field>
                </div>

                {/* ── Guardar ─────────────────────────────────────────────── */}
                <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all"
                    style={{ backgroundColor: '#2284DF' }}
                    onMouseEnter={e => !saving && (e.currentTarget.style.backgroundColor = '#1a6eb8')}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2284DF'}
                >
                    {saving
                        ? <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</>
                        : <><Save className="w-4 h-4" /> Guardar cambios</>
                    }
                </button>
            </form>

        </div>
    );
};

export default Settings;
